import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Info, Loader2, Lock, Tag } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/format";
import { useSeo } from "@/lib/seo";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";

export default function Checkout() {
  const { items, clearCart, currency } = useStore();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "Côte d'Ivoire", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [placed, setPlaced] = useState(false);

  useSeo({ title: "Finaliser ma commande — ISMO-TECH", noIndex: true });

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "", phone: f.phone || user.phone || "" }));
  }, [user]);

  const payload = useMemo(
    () => items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, fields: i.fields || {} })),
    [items],
  );

  useEffect(() => {
    if (!items.length) return;
    let active = true;
    api
      .post("/cart/validate", { items: payload, promo_code: appliedPromo, currency })
      .then(({ data }) => active && setSummary(data))
      .catch((error) => toast.error(apiError(error)));
    return () => {
      active = false;
    };
  }, [payload, appliedPromo, currency, items.length]);

  useEffect(() => {
    if (!items.length && !placed) navigate("/panier");
  }, [items.length, navigate, placed]);

  const applyPromo = async () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    try {
      const { data } = await api.post("/cart/validate", { items: payload, promo_code: code, currency });
      if (data.promo_valid) {
        setAppliedPromo(code);
        toast.success(`Code ${code} appliqué : -${data.promo_percent}%`);
      } else {
        toast.error("Code promo invalide ou conditions non remplies");
      }
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (form.phone && !isValidPhoneNumber(form.phone)) {
      toast.error("Numéro de téléphone invalide. Vérifiez l'indicatif du pays et le nombre de chiffres.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        items: payload,
        customer: { name: form.name, email: form.email, phone: form.phone, country: form.country },
        payment_method: "manual",
        promo_code: appliedPromo,
        currency,
        notes: form.notes,
      });
      setPlaced(true);
      navigate(`/paiement/${data.order_number}`, { state: { order: data, email: form.email } });
      clearCart();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-it py-6 lg:py-12">
      {loading ? null : !user ? (
        <Navigate to="/connexion" replace state={{ from: "/commander" }} />
      ) : null}
      <h1 className="font-display text-2xl font-extrabold text-ink sm:text-4xl" data-testid="checkout-title">
        Finaliser ma commande
      </h1>
      <p className="mt-1.5 text-sm text-slate-500">
        Votre commande est traitée manuellement par notre équipe après validation.
      </p>

      <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-[1fr_400px] lg:gap-10">
        <div className="space-y-6">
          <section className="rounded-2xl border border-line bg-white p-6">
            <p className="font-display text-lg font-bold text-ink">1. Vos informations</p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
                  Nom complet
                </span>
                <input
                  data-testid="checkout-name"
                  required
                  minLength={2}
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                />
              </label>
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Email</span>
                <input
                  data-testid="checkout-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                />
              </label>
              <PhoneField
                testId="checkout"
                label="Téléphone"
                value={form.phone}
                onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              />
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Pays</span>
                <input
                  data-testid="checkout-country"
                  value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10"
                />
              </label>
            </div>
            {!user && (
              <p className="mt-4 rounded-xl bg-brand-soft p-3 text-xs text-brand">
                Astuce : <Link to="/connexion" className="font-bold underline">connectez-vous</Link> pour suivre
                votre commande et recevoir votre livraison dans votre espace client.
              </p>
            )}
          </section>

          <section className="rounded-2xl border border-line bg-white p-6" data-testid="checkout-payment-info">
            <p className="font-display text-lg font-bold text-ink">2. Paiement</p>
            <div className="mt-4 flex items-start gap-3 rounded-xl border border-brand/20 bg-brand-soft p-4">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
              <div className="text-xs leading-relaxed text-slate-600">
                <p className="font-bold text-ink">Le paiement s'effectue à l'étape suivante.</p>
                <p className="mt-1">
                  Après validation de votre commande, vous accéderez à la page de paiement sécurisée pour choisir
                  votre moyen de paiement (Mobile Money, carte bancaire…). Le montant exact à payer est calculé
                  automatiquement par nos serveurs.
                </p>
              </div>
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
              <Lock className="h-3.5 w-3.5" /> Connexion chiffrée. Vos données ne sont jamais partagées.
            </p>
          </section>

          <section className="rounded-2xl border border-line bg-white p-6">
            <p className="font-display text-lg font-bold text-ink">3. Code promo & notes</p>
            <div className="mt-4 flex gap-2">
              <input
                data-testid="promo-input"
                value={promoInput}
                onChange={(e) => setPromoInput(e.target.value)}
                placeholder="Ex : ISMO10"
                className="h-12 flex-1 rounded-xl border border-line px-4 text-sm uppercase focus:border-brand focus:outline-none"
              />
              <button
                type="button"
                data-testid="promo-apply"
                onClick={applyPromo}
                className="flex items-center gap-2 rounded-xl border-2 border-ink px-5 text-sm font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
              >
                <Tag className="h-4 w-4" /> Appliquer
              </button>
            </div>
            <textarea
              data-testid="checkout-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              placeholder="Informations utiles pour votre commande (optionnel)"
              className="mt-3 w-full rounded-xl border border-line p-4 text-sm focus:border-brand focus:outline-none"
            />
          </section>
        </div>

        <aside className="lg:sticky lg:top-[150px] lg:h-fit">
          <div className="rounded-2xl border border-line bg-white p-6">
            <p className="font-display text-lg font-bold text-ink">Votre commande</p>

            <ul className="mt-4 space-y-3 border-b border-line pb-4">
              {items.map((item) => (
                <li key={item.product_id} className="flex items-center gap-3">
                  <img
                    src={item.image_url}
                    alt={item.name}
                    className="h-12 w-12 rounded-lg border border-line bg-slate-50 object-contain p-1"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-xs font-semibold text-ink">{item.name}</span>
                    <span className="text-[11px] text-slate-500">Qté {item.quantity}</span>
                  </span>
                  <span className="text-xs font-bold text-ink">
                    {formatPrice(item.price * item.quantity, currency)}
                  </span>
                </li>
              ))}
            </ul>

            <dl className="mt-4 space-y-2.5 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">Sous-total</dt>
                <dd className="font-semibold text-ink">{formatPrice(summary?.subtotal || 0, currency)}</dd>
              </div>
              {summary?.discount > 0 && (
                <div className="flex justify-between">
                  <dt className="text-slate-500">Code {summary.promo_code}</dt>
                  <dd className="font-semibold text-emerald-600" data-testid="checkout-discount">
                    −{formatPrice(summary.discount, currency)}
                  </dd>
                </div>
              )}
              <div className="flex items-baseline justify-between border-t border-line pt-3">
                <dt className="font-bold text-ink">Total</dt>
                <dd className="font-display text-2xl font-black text-brand" data-testid="checkout-total">
                  {formatPrice(summary?.total || 0, currency)}
                </dd>
              </div>
            </dl>

            <button
              type="submit"
              data-testid="place-order-btn"
              disabled={submitting || !summary}
              className="btn-glow mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {submitting ? "Traitement..." : "Valider la commande"}
            </button>
            <p className="mt-3 text-center text-[11px] text-slate-400">
              En validant, vous acceptez nos conditions générales de vente.
            </p>
          </div>
        </aside>
      </form>
    </div>
  );
}
